/**
 * 手柄变换的**世界矩阵**（纯函数）。
 *
 * 统一公式（PLAN §6.3 的"夹心乘法"）：
 *   元素新 transform = inv(M父) · W · M父 · 旧transform
 * W 是"在根用户坐标系里想做的变换"（用户屏幕上看到的那件事）。
 * 本文件只负责算 W 与做共轭，不碰 DOM —— 因此可以在 jsdom 里断言，
 * 也是 M1 那些"拖 100 次无 NaN""缩放比例正确"的验收基础。
 */
import { parseTransformList } from './transform';
import {
  IDENTITY,
  around,
  invert,
  isFiniteMat,
  mul,
  rotate,
  scale,
  uniform,
  type Mat,
  type Pt,
  type Rect,
} from './matrix';

export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export const MIN_SCALE = 1e-3;

/** 手柄在框上的归一化位置：0=左/上，0.5=中，1=右/下 */
const axisFactor = (handle: HandleId): { x: number; y: number } => ({
  x: handle.includes('w') ? 0 : handle.includes('e') ? 1 : 0.5,
  y: handle.includes('n') ? 0 : handle.includes('s') ? 1 : 0.5,
});

/** 缩放锚点：默认取对角（对边）固定点；Alt（fromCenter）取中心 */
export function anchorOf(box: Rect, handle: HandleId, fromCenter = false): Pt {
  if (fromCenter) return { x: box.x + box.w / 2, y: box.y + box.h / 2 };
  const f = axisFactor(handle);
  return { x: box.x + (1 - f.x) * box.w, y: box.y + (1 - f.y) * box.h };
}

/** 除法守卫：分母接近 0 时返回 1（拖到手柄与锚点重合不能产生 Infinity） */
const ratio = (num: number, den: number): number => {
  if (Math.abs(den) < 1e-9) return 1;
  const r = num / den;
  return Number.isFinite(r) ? r : 1;
};

const clampScale = (s: number): number => {
  if (!Number.isFinite(s)) return 1;
  const abs = Math.max(Math.abs(s), MIN_SCALE);
  return s < 0 ? -abs : abs;
};

export interface ResizeInput {
  handle: HandleId;
  /** 按下时的指针（根用户坐标） */
  start: Pt;
  /** 当前指针（根用户坐标） */
  cur: Pt;
  /** 按下时的选择框（根用户坐标，轴对齐） */
  box: Rect;
  /** Shift：等比 */
  uniform?: boolean;
  /** Alt：以中心为锚 */
  fromCenter?: boolean;
}

export function resizeWorldMatrix({ handle, start, cur, box, uniform: uni, fromCenter }: ResizeInput): Mat {
  const f = axisFactor(handle);
  const anchor = anchorOf(box, handle, fromCenter);

  let sx = f.x === 0.5 ? 1 : ratio(cur.x - anchor.x, start.x - anchor.x);
  let sy = f.y === 0.5 ? 1 : ratio(cur.y - anchor.y, start.y - anchor.y);

  if (uni) {
    if (f.x === 0.5 || f.y === 0.5) {
      // 边中手柄 + Shift：两轴同倍（取当前变化轴）
      const k = f.x === 0.5 ? sy : sx;
      sx = k;
      sy = k;
    } else {
      const u = uniform(sx, sy);
      sx = u.sx;
      sy = u.sy;
    }
  }

  sx = clampScale(sx);
  sy = clampScale(sy);
  return around(scale(sx, sy), anchor.x, anchor.y);
}

export interface RotateInput {
  /** 选择框中心（根用户坐标） */
  center: Pt;
  start: Pt;
  cur: Pt;
  /** Shift：按该角度吸附（如 15） */
  snapDeg?: number;
}

export function rotateWorldMatrix({ center, start, cur, snapDeg = 0 }: RotateInput): Mat {
  // 力臂为零（按下点就在中心）→ 无从测量初始角，宁可不动
  if (Math.hypot(start.x - center.x, start.y - center.y) < 1e-9) return IDENTITY;
  const a0 = Math.atan2(start.y - center.y, start.x - center.x);
  const a1 = Math.atan2(cur.y - center.y, cur.x - center.x);
  if (!Number.isFinite(a0) || !Number.isFinite(a1)) return IDENTITY;
  let deg = ((a1 - a0) * 180) / Math.PI;
  if (snapDeg > 0) deg = Math.round(deg / snapDeg) * snapDeg;
  return around(rotate(deg), center.x, center.y);
}

/** 把世界变换共轭到元素父坐标系：inv(M父) · W · M父 */
export function conjugateWorld(W: Mat, parentMat: Mat): Mat {
  const inv = invert(parentMat);
  if (!inv) return W; // 父坐标系退化（缩放为 0）→ 不勉强换算
  return mul(mul(inv, W), parentMat);
}

/**
 * 拆出 transform 里的全部函数项；含有无法识别的杂质时返回 null（此时不合并，保持原样）
 */
function termsOf(value: string): string[] | null {
  const re = /([a-zA-Z]+)\s*\(([^)]*)\)/g;
  const terms: string[] = [];
  let last = 0;
  let hit: RegExpExecArray | null;
  while ((hit = re.exec(value)) !== null) {
    if (value.slice(last, hit.index).trim() !== '') return null;
    terms.push(hit[0]);
    last = re.lastIndex;
  }
  return value.slice(last).trim() === '' && terms.length > 0 ? terms : null;
}

/**
 * 元素新的 transform 属性值。
 *
 * 两种情形：
 *  ① 原有写法里**全是 matrix(...)**（= 本编辑器此前叠加的结果）→ 合并成一个矩阵，
 *    否则连按方向键/连续拖滑杆会让 transform 无限堆叠成一长串（文件会被改脏）。
 *  ② 含有用户手写的 rotate/translate/... → 原样保留，只把新矩阵**前置**叠加
 *    （前置拼接的乘法顺序天然正确，也不会破坏作者意图）。
 */
export function nextTransformAttribute(existing: string | null, W: Mat, parentMat: Mat): string {
  const local = conjugateWorld(W, parentMat);
  const head = matToAttr(local);
  const trimmed = existing?.trim() ?? '';
  if (trimmed === '') return head;

  const terms = termsOf(trimmed);
  if (terms) {
    // 只合并**前导**的 matrix 串（那是编辑器自己叠加的），后面用户手写的项原样保留
    let acc = local;
    let n = 0;
    while (n < terms.length && /^matrix\s*\(/i.test(terms[n] as string)) {
      acc = mul(acc, parseTransformList(terms[n] as string).mat);
      n += 1;
    }
    if (n > 0) {
      const rest = terms.slice(n).join(' ');
      return rest === '' ? matToAttr(acc) : `${matToAttr(acc)} ${rest}`;
    }
  }
  return `${head} ${trimmed}`;
}

/** 与 matrix.toString 同源，但先做有限性守卫（脏矩阵一律回落为单位矩阵） */
function matToAttr(m: Mat): string {
  const safe: Mat = isFiniteMat(m) ? m : IDENTITY;
  return `matrix(${safe.map((v) => +v.toFixed(6)).join(' ')})`;
}

/** 网格吸附：把结果位置吸附到格点上（对"位置"吸附而非对"位移"吸附，避免连续拖动累积漂移） */
export function snapValue(value: number, grid: number): number {
  if (!Number.isFinite(value) || !(grid > 0)) return value;
  return Math.round(value / grid) * grid;
}

export const snapDelta = (delta: number, grid: number): number =>
  Number.isFinite(delta) && grid > 0 ? snapValue(delta, grid) : delta;
